
import { useMemo } from "react";
import { Movie, PendingAction } from "@/types/movie";
import { isDebugMovie } from "@/utils/debugLogger";
import { LIST_NAMES } from "@/hooks/search/types/listConstants";

/**
 * Hook for tracking pending actions related to a movie card
 * Pure TMDB ID system - no legacy ID support
 */
export const usePendingActionTracking = (
  movie: Movie,
  pendingActions: PendingAction[],
  currentTab?: string,
  isSearchResult = false
) => {
  const shouldDebug = isDebugMovie(movie);
  
  // DEBUG: Log the inputs
  console.log(`🔍 usePendingActionTracking for "${movie.title}":`, {
    tmdbId: movie.tmdbId,
    currentTab,
    isSearchResult,
    totalPendingActions: pendingActions.length,
    pendingActionTypes: pendingActions.map(a => ({ movieId: a.movieId, type: a.type, fromSection: a.fromSection }))
  });
  
  // Create a consistent lookup key using TMDB ID only
  const lookupKey = useMemo(() => {
    return `tmdb_${movie.tmdbId}`;
  }, [movie.tmdbId]);

  // Find any pending action for this movie using TMDB ID
  const pendingAction = useMemo(() => {
    if (isSearchResult) {
      console.log(`🔍 "${movie.title}" is search result, no pending action`);
      return null;
    }
    
    const matchingActions = pendingActions.filter(action => {
      const isMatchingMovie = action.movieId === movie.tmdbId;
      console.log(`🔍 Checking action for movie ${action.movieId} vs ${movie.tmdbId}:`, {
        isMatchingMovie,
        actionType: action.type,
        fromSection: action.fromSection
      });
      return isMatchingMovie;
    });
    
    console.log(`🔍 Found ${matchingActions.length} matching actions for "${movie.title}"`);
    
    // If no currentTab, show any pending action for this movie
    if (!currentTab) {
      const action = matchingActions[0] || null;
      console.log(`🔍 No currentTab, returning first action:`, action?.id || 'none');
      return action;
    }
    
    console.log(`🔍 Filtering by currentTab: "${currentTab}"`);
    
    const relevantAction = matchingActions.find(action => {
      // Check if the pending action is relevant to the current tab
      const isRelevantToTab = 
        (currentTab === LIST_NAMES.FAVORITES && action.type === 'favorite') ||
        (currentTab === LIST_NAMES.WATCHLIST && action.type === 'planToWatch') ||
        (currentTab === LIST_NAMES.UNSEEN_GEMS && action.type === 'favorite');
      
      // FIXED: Also check that the action originated from the current tab
      const isFromCurrentTab = action.fromSection === currentTab;
      
      console.log(`🔍 Action relevance check:`, {
        currentTab,
        actionType: action.type,
        fromSection: action.fromSection,
        isRelevantToTab,
        isFromCurrentTab,
        bothConditionsMet: isRelevantToTab && isFromCurrentTab,
        LIST_NAMES_WATCHLIST: LIST_NAMES.WATCHLIST,
        LIST_NAMES_FAVORITES: LIST_NAMES.FAVORITES,
        LIST_NAMES_UNSEEN_GEMS: LIST_NAMES.UNSEEN_GEMS
      });
      
      // Return true only if both conditions are met
      return isRelevantToTab && isFromCurrentTab;
    });
    
    console.log(`🔍 Final relevant action for "${movie.title}":`, relevantAction?.id || 'none');
    return relevantAction || null;
  }, [pendingActions, movie.tmdbId, currentTab, isSearchResult, movie.title]);

  return {
    lookupKey,
    pendingAction,
    shouldDebug
  };
};
